import { Component } from 'preact';
import { motion, AnimatePresence } from 'framer-motion';
import CardInspector from './CardInspector';
import { pushDismiss, removeDismiss } from '../utils/escStack';
import VikingOrnament from './VikingOrnament';
import { resolveLocalImageUrl } from '../utils/localApi';
import {
  GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, ACCENT_GOLD,
  DIALOG_STYLE, INPUT_STYLE, FourCorners, OrnamentalDivider,
} from '../lib/medievalTheme';
import { playUI, UI } from '../utils/arena/uiSounds';
import {
  ART_GROUP_BASE, printingsInArtGroup, artGroupLabel, artGroupDisplayName,
} from '../utils/sorcery/artGroups';
import { t } from '../localization/t';

// Reusable card-picker overlay for "attach a card" flows. Opens as a
// floating panel docked to the left of whichever host panel spawned
// it (ChatPanel for chat attachments, SocialPanel's compose for mail
// attachments). Host passes `right` to place the picker on-screen.
//
// The picker is stateless about the selection itself. The host owns
// the selected-cards list and provides onAddCard / onRemoveCard
// callbacks so the same component can drive chat attachments
// (ChatView.state.attachCards) or mail attachments
// (Mailbox.state.composeCards) without knowing which.

// Collection rows are (card, art group, foiling), so a card the player owns
// in two illustrations arrives as two rows. Resolving the image off
// printings[0] would draw both as the base art, and both tiles would key and
// select identically. Attaching one would silently spend the other.
function resolveCardImage(cardId, artGroup, foiling, sorceryCards) {
  if (!cardId || !sorceryCards) return null;
  const card = sorceryCards.find((c) => c.unique_id === cardId);
  if (!card) return null;
  const inGroup = printingsInArtGroup(card, artGroup || ART_GROUP_BASE);
  const printing = inGroup.find((p) => (p.foiling || 'S') === (foiling || 'S')) || inGroup[0];
  return printing?.image_url || card.printings?.[0]?.image_url || null;
}

function resolveCardName(cardId, artGroup, sorceryCards) {
  if (!cardId || !sorceryCards) return cardId;
  const card = sorceryCards.find((c) => c.unique_id === cardId);
  if (!card) return cardId;
  return artGroupDisplayName(card, artGroup || ART_GROUP_BASE) || cardId;
}

export default class CardPickerPanel extends Component {
  state = { search: '', inspectedCard: null };

  componentDidUpdate(prevProps, prevState) {
    // Reset the search when reopened so the next attach starts clean.
    if (!prevProps.open && this.props.open) {
      this.setState({ search: '', inspectedCard: null });
    }
    // The card inspector owns Escape via the central stack while open.
    if (!prevState.inspectedCard && this.state.inspectedCard) {
      this._escDismissId = pushDismiss(() => this.setState({ inspectedCard: null }));
    } else if (prevState.inspectedCard && !this.state.inspectedCard) {
      removeDismiss(this._escDismissId);
    }
  }

  componentWillUnmount() {
    removeDismiss(this._escDismissId);
  }

  render() {
    const {
      open, right = '27cqw', profile, sorceryCards,
      selectedCards = [], maxSelection = 10,
      onAddCard, onRemoveCard, onClose,
    } = this.props;
    const { search } = this.state;
    const collection = profile?.collection || [];

    const searchLower = search.toLowerCase();
    const filtered = searchLower
      ? collection.filter((entry) => resolveCardName(entry.cardId, entry.artGroup, sorceryCards).toLowerCase().includes(searchLower))
      : collection;

    return (
      <>
        <AnimatePresence>
          {open ? (
            <motion.div
              key="card-picker"
              className="fixed z-[62] flex flex-col pointer-events-auto"
              data-screenshot-ignore="true"
              initial={{ opacity: 0, x: 12, y: '-50%', scale: 0.96 }}
              animate={{ opacity: 1, x: 0, y: '-50%', scale: 1 }}
              exit={{ opacity: 0, x: 12, y: '-50%', scale: 0.96 }}
              transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
              style={{
                right,
                top: '50%',
                width: '20cqw',
                height: '60cqh',
                transformOrigin: 'center right',
                                ...DIALOG_STYLE,
              }}
            >
              <FourCorners />
              <VikingOrnament ornament="broa016" variant="centerpiece" opacity={0.03} />

              <div className="px-4 pt-4 pb-2 shrink-0">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold arena-heading" style={{ color: TEXT_PRIMARY }}>
                    {t('Attach Cards')}
                  </span>
                  <button
                    type="button"
                    className="w-5 h-5 rounded-full flex items-center justify-center cursor-pointer transition-colors"
                    style={{ color: TEXT_MUTED }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = TEXT_BODY; e.currentTarget.style.background = `${GOLD} 0.08)`; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = TEXT_MUTED; e.currentTarget.style.background = 'transparent'; }}
                    onClick={() => onClose?.()}
                  >
                    <svg viewBox="0 0 14 14" fill="none" style={{ width: '0.625cqw', height: '0.625cqw' }}>
                      <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </button>
                </div>
                <input
                  type="text"
                  placeholder={t('Search cards...')}
                  value={search}
                  className="w-full px-2.5 py-1.5 text-xs outline-none"
                  style={{ ...INPUT_STYLE, borderRadius: 'var(--radius-md)', color: TEXT_PRIMARY }}
                  onInput={(e) => this.setState({ search: e.target.value })}
                />
              </div>

              <OrnamentalDivider className="px-4 my-1" />

              <div className="flex-1 min-h-0 overflow-y-auto px-3 py-2">
                <div className="grid grid-cols-4 gap-2">
                  {filtered.map((entry) => {
                    const foiling = entry.foiling || 'S';
                    const artGroup = entry.artGroup || ART_GROUP_BASE;
                    const rawImg = resolveCardImage(entry.cardId, artGroup, foiling, sorceryCards);
                    const imgUrl = rawImg ? resolveLocalImageUrl(rawImg) : null;
                    const name = resolveCardName(entry.cardId, artGroup, sorceryCards);
                    const groupLabel = artGroupLabel(artGroup);
                    const selectedCount = selectedCards.filter((c) =>
                      c.cardId === entry.cardId
                      && (c.artGroup || ART_GROUP_BASE) === artGroup
                      && c.foiling === foiling
                    ).length;
                    const isSelected = selectedCount > 0;
                    const canAddMore = selectedCount < entry.quantity && selectedCards.length < maxSelection;
                    const isFoil = foiling === 'F' || foiling === 'R';
                    const foilColor = foiling === 'R' ? '#c480e0' : ACCENT_GOLD;
                    return (
                      <div key={`${entry.cardId}-${artGroup}-${foiling}`} className="relative">
                        <button
                          type="button"
                          className="relative w-full rounded-card overflow-hidden transition-all cursor-pointer"
                          style={isSelected
                            ? { border: `2px solid ${isFoil ? foilColor : ACCENT_GOLD}`, boxShadow: `0 0 10px ${GOLD} 0.25)` }
                            : { border: `2px solid ${isFoil ? foilColor + '55' : `${GOLD} 0.1)`}` }}
                          onClick={() => {
                            if (!canAddMore) return;
                            onAddCard?.(entry.cardId, artGroup, foiling);
                          }}
                        >
                          {imgUrl ? (
                            <img src={imgUrl} alt={name} className="w-full aspect-[5/7] object-cover" />
                          ) : (
                            <div className="w-full aspect-[5/7] flex items-center justify-center text-[0.5cqw] p-1 text-center" style={{ background: `${GOLD} 0.04)`, color: TEXT_MUTED }}>
                              {name}
                            </div>
                          )}
                          <span
                            className="absolute top-1 right-1 text-[0.5cqw] font-bold px-1 py-0.5 rounded"
                            style={{ background: 'rgba(0,0,0,0.8)', color: TEXT_BODY }}
                          >
                            x{entry.quantity - selectedCount}
                          </span>
                          {isFoil && (
                            <span
                              className="absolute top-1 left-1 text-[0.5cqw] font-bold px-1 py-0.5 rounded"
                              style={{ background: 'rgba(0,0,0,0.8)', color: foilColor }}
                            >
                              {foiling === 'R' ? 'RF' : 'F'}
                            </span>
                          )}
                          {groupLabel && (
                            <span
                              className="absolute top-1 left-1 text-[0.4375cqw] font-bold px-1 py-0.5 rounded truncate max-w-[70%]"
                              style={{ background: 'rgba(0,0,0,0.8)', color: ACCENT_GOLD, top: isFoil ? '1.4cqw' : undefined }}
                            >
                              {groupLabel}
                            </span>
                          )}
                          {isSelected && (
                            <div className="absolute bottom-7 left-1 min-w-[1.125cqw] h-[1.125cqw] rounded flex items-center justify-center text-[0.5625cqw] font-bold" style={{ background: ACCENT_GOLD, color: '#1a1408', boxShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>
                              {selectedCount}
                            </div>
                          )}
                          <div
                            className="absolute bottom-0 left-0 right-0 text-[0.4375cqw] text-center py-0.5 truncate px-0.5"
                            style={{ background: 'rgba(0,0,0,0.75)', color: TEXT_BODY }}
                          >
                            {name}
                          </div>
                        </button>
                        {isSelected && (
                          <button
                            type="button"
                            className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center text-[0.5625cqw] font-bold cursor-pointer z-10"
                            style={{ background: '#c45050', color: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.5)' }}
                            onClick={() => onRemoveCard?.(entry.cardId, artGroup, foiling)}
                          >
                            −
                          </button>
                        )}
                      </div>
                    );
                  })}
                  {filtered.length === 0 && (
                    <div className="col-span-4 text-[0.625cqw] py-8 text-center" style={{ color: TEXT_MUTED }}>
                      {search ? t('No cards match your search') : t('No cards in collection')}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>
        {this.state.inspectedCard && (
          <CardInspector
            card={this.state.inspectedCard.card}
            imageUrl={this.state.inspectedCard.printing?.image_url}
            rarity={this.state.inspectedCard.rarity}
            foiling={this.state.inspectedCard.printing?.foiling}
            onClose={() => { playUI(UI.INSPECTOR_CLOSE); this.setState({ inspectedCard: null }); }}
          />
        )}
      </>
    );
  }
}
